import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  GuidedTourSpotlightTextWrapper,
  GuidedTourSpotlightWrapper,
} from './style';
import { BlurBackgroundDivFixed } from 'crono-fe-common/components/Layout/BlurBackgroundDiv';
import { ClickAwayListener, Typography } from '@mui/material';
import { colors } from 'crono-fe-common/theme';
import { createPortal } from 'react-dom';
import useGetUserGuidedTours from 'hooks/services/user/useGetUserGuidedTours';
import usePatchUserGuidedTours from 'hooks/services/user/usePatchUserGuidedTours';
import {
  IGetUserGuidedTours,
  GuidedToursSteps,
} from 'crono-fe-common/types/guidedTours';
import CloseSIcon from 'crono-fe-common/icons/Icon-Close-S';

interface IProps {
  children: ReactElement;
  style?: React.CSSProperties;
  title: string;
  subtitle?: string;
  direction: 'top' | 'bottom' | 'left' | 'right';
  positionOffset?: Position;
  positionOffsetArrow?: Position;
  guidedTourProperty: keyof IGetUserGuidedTours;
  valueToSpotlightOn: GuidedToursSteps | false | null;

  additionalConditionToWait?: {
    guidedTourProperty: keyof IGetUserGuidedTours;
    valueToAvoid: GuidedToursSteps | false | null;
  };

  disabled?: boolean;
  extractIsVisible?: (value: boolean) => void;
  disableForMultipleSpotlight?: boolean;

  //To position initially the text of the spotlight
  defaultPosition?: Position;

  //If we want to patch the currentStep in a different situation, with a signal coming from the parent
  disableAutoPatch?: boolean;
  signalPatch?: boolean;
}

interface Position {
  left?: number;
  top?: number;
  right?: number;
  bottom?: number;
}

interface PositionGuaranteed {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

const GuidedTourSpotlight = ({
  children,
  style,
  title,
  subtitle,
  direction,
  guidedTourProperty,
  valueToSpotlightOn,
  additionalConditionToWait,
  disabled = false,
  extractIsVisible,
  positionOffset,
  positionOffsetArrow,
  disableForMultipleSpotlight = false,
  disableAutoPatch = false,
  signalPatch = false,
  defaultPosition,
}: IProps) => {
  const { data: userGuidedTours } = useGetUserGuidedTours();

  //[INFO] Guided tour is hidden  on the platform, starting from Jan 8, 2026. Need to be re-enabled in the future.
  //If something that is not undefined, show the guided tour
  const guidedTourCurrentValue = useMemo(() => {
    return undefined;
    // if (!userGuidedTours?.data.data || disabled) return;
    //
    // const guidedTour = userGuidedTours.data.data[guidedTourProperty];
    //
    // if (guidedTour !== valueToSpotlightOn) {
    //   return undefined;
    // }
    //
    // //Check for additional condition
    // if (additionalConditionToWait) {
    //   const guidedTourToAvoid =
    //     userGuidedTours.data.data[additionalConditionToWait.guidedTourProperty];
    //   if (guidedTourToAvoid === additionalConditionToWait.valueToAvoid) {
    //     return undefined;
    //   }
    // }
    // return guidedTour;
  }, [
    guidedTourProperty,
    valueToSpotlightOn,
    userGuidedTours,
    disabled,
    additionalConditionToWait,
  ]);
  const [showGuidedTour, setShowGuidedTour] = useState<boolean>(false);
  useEffect(() => {
    const newGuidedTour = guidedTourCurrentValue !== undefined;
    setShowGuidedTour(newGuidedTour);
    if (newGuidedTour) {
      if (defaultPosition) {
        setPosition(defaultPosition);
      }
    }
  }, [guidedTourCurrentValue]);
  useEffect(() => {
    extractIsVisible?.(showGuidedTour);
    if (showGuidedTour) {
      const scrollAndPosition = () => {
        ref.current?.scrollIntoView({
          block: 'center',
          inline: 'center',
        });
        triggerReposition();
      };
      //Scroll both immediately and after a second so that we can catch eventually loading components
      scrollAndPosition();

      setTimeout(() => {
        scrollAndPosition();
      }, 1000);
    }
  }, [showGuidedTour, extractIsVisible]);

  const [position, setPosition] = useState<Position>({ left: 0, top: 0 });
  const [arrowPosition, setArrowPosition] = useState<Position>({
    left: 0,
    top: 0,
  });
  const ref = useRef<HTMLDivElement>(null);

  const triggerReposition = () => {
    const value = ref.current;
    if (!value) return;
    let newPosition: Position = { left: 0, top: 0 };
    let newArrowPosition: Position = { left: 0, top: 0 };
    const positionOffsetValue: PositionGuaranteed = {
      left: positionOffset?.left || 0,
      top: positionOffset?.top || 0,
      bottom: positionOffset?.bottom || 0,
      right: positionOffset?.right || 0,
    };
    const positionOffsetArrowValue: PositionGuaranteed = {
      left: positionOffsetArrow?.left || 0,
      top: positionOffsetArrow?.top || 0,
      bottom: positionOffsetArrow?.bottom || 0,
      right: positionOffsetArrow?.right || 0,
    };
    switch (direction) {
      case 'right':
        newPosition = {
          left:
            (value.getBoundingClientRect().right + 16 || 0) +
            positionOffsetValue.left,
          top:
            (value.getBoundingClientRect().top + 16 || 0) +
            positionOffsetValue.top,
        };
        newArrowPosition = {
          left: -4 + positionOffsetArrowValue.left,
          top: 20 + positionOffsetArrowValue.top,
        };
        break;
      case 'left':
        newPosition = {
          left:
            (value.getBoundingClientRect().left - 276 || 0) +
            positionOffsetValue.left,
          top:
            (value.getBoundingClientRect().top + 16 || 0) +
            positionOffsetValue.top,
        };
        newArrowPosition = {
          right: -4 + positionOffsetArrowValue.right,
          top: 20 + positionOffsetArrowValue.top,
        };
        break;
      case 'top':
        newPosition = {
          left:
            (value.getBoundingClientRect().left + 16 || 0) +
            positionOffsetValue.left,
          top:
            (value.getBoundingClientRect().top - 124 || 0) +
            positionOffsetValue.top,
        };
        newArrowPosition = {
          left: 20 + positionOffsetArrowValue.left,
          bottom: -4 + positionOffsetArrowValue.bottom,
        };
        break;
      case 'bottom':
        newPosition = {
          left:
            (value.getBoundingClientRect().left + 16 || 0) +
            positionOffsetValue.left,
          top:
            (value.getBoundingClientRect().bottom + 16 || 0) +
            positionOffsetValue.top,
        };
        newArrowPosition = {
          left: 20 + positionOffsetArrowValue.left,
          top: -4 + positionOffsetArrowValue.top,
        };
        break;
    }
    setPosition(newPosition);
    setArrowPosition(newArrowPosition);
  };
  useEffect(() => {
    triggerReposition();
  }, [direction, positionOffset, positionOffsetArrow]);

  const { mutate: patchUserGuidedTours } = usePatchUserGuidedTours();

  const skipCurrentGuidedTourStep = useCallback(() => {
    if (!showGuidedTour) return;
    //Find next value to be placed
    const newValue = null;
    patchUserGuidedTours({
      [guidedTourProperty]: newValue,
    });
    setShowGuidedTour(false);
  }, [guidedTourCurrentValue, patchUserGuidedTours, showGuidedTour]);

  useEffect(() => {
    if (disableAutoPatch && showGuidedTour && signalPatch) {
      skipCurrentGuidedTourStep();
    }
  }, [
    disableAutoPatch,
    signalPatch,
    showGuidedTour,
    skipCurrentGuidedTourStep,
  ]);

  return (
    <>
      {showGuidedTour && !disableForMultipleSpotlight && (
        <BlurBackgroundDivFixed
          onClick={(ev) => {
            ev.stopPropagation();
            ev.preventDefault();
            skipCurrentGuidedTourStep();
          }}
          style={{
            backgroundColor: `${colors.black}55`,
          }}
        />
      )}
      {showGuidedTour ? (
        <ClickAwayListener
          onClickAway={(ev) => {
            ev.stopPropagation();
            ev.preventDefault();
            skipCurrentGuidedTourStep();
          }}
        >
          <GuidedTourSpotlightWrapper
            style={style}
            ref={ref}
            onClick={(ev) => {
              ev.stopPropagation();
              ev.preventDefault();
              if (disableAutoPatch) return;
              skipCurrentGuidedTourStep();
            }}
          >
            {showGuidedTour &&
              !disableForMultipleSpotlight &&
              createPortal(
                <GuidedTourSpotlightTextWrapper style={position}>
                  <div
                    className="guided-tour-spotlight-arrow"
                    style={arrowPosition}
                  />
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      flex: 1,
                    }}
                  >
                    <Typography
                      fontSize={16}
                      lineHeight={'24px'}
                      fontWeight={600}
                    >
                      {title}
                    </Typography>
                    {subtitle && (
                      <Typography
                        fontSize={12}
                        lineHeight={'16px'}
                        color={colors.grey11}
                      >
                        {subtitle}
                      </Typography>
                    )}
                  </div>
                  <CloseSIcon
                    color={colors.grey2}
                    className="close-icon"
                    onClick={skipCurrentGuidedTourStep}
                  />
                </GuidedTourSpotlightTextWrapper>,
                document.body,
              )}
            {children}
          </GuidedTourSpotlightWrapper>
        </ClickAwayListener>
      ) : (
        children
      )}
    </>
  );
};

export default GuidedTourSpotlight;
